import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateCuring } from "./curing";
import { api } from "../utils/api";
import { ApiResponse, ApiError } from "../types/api";
import { VegetationType } from "../types/vegetationType";
import { useParams } from "react-router-dom";
import { bumpVegetationTileVersion } from "./vegetation";

/** A project's vegetation types by explicit id (story pages off the project route). */
export const useVegetationTypesFor = (projectId: number | string | null | undefined) => {
  const idKey = projectId !== null && projectId !== undefined ? String(projectId) : undefined;
  return useQuery<ApiResponse<VegetationType[]>, ApiError>({
    queryKey: ["vegetationTypes", idKey],
    queryFn: () =>
      api.request<ApiResponse<VegetationType[]>>(`/api/projects/${idKey}/vegetationTypes`),
    enabled: !!idKey,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useVegetationTypes = () => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery<ApiResponse<VegetationType[]>, ApiError>({
    queryKey: ["vegetationTypes", projectId],
    queryFn: () =>
      api.request<ApiResponse<VegetationType[]>>(
        `/api/projects/${projectId}/vegetationTypes`
      ),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

/** One vegetation type's fields as the server takes them. */
export interface VegetationTypeFields {
  veg_class?: string;
  max_fuel_load?: number;
  accumulation_rate?: number;
  /** 0 when the type has no fire model; otherwise send model_class_name. */
  model_class?: number;
  model_class_name?: string | null;
  model_subclass?: number | null;
  color?: string | null;
}

export interface VegetationTypeBatch {
  delete: number[];
  update: (VegetationTypeFields & { id: number })[];
  /** ``key`` names the draft row in a rejection's ``fields``. */
  create: (VegetationTypeFields & { key: string })[];
}

/** Save a set of edits together, all or nothing (the Vegetation types tab). */
export const useSaveVegetationTypes = () => {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();

  return useMutation<ApiResponse<VegetationType[]>, ApiError & { data?: { fields?: Record<string, string> } }, VegetationTypeBatch>({
    mutationFn: (batch) =>
      api.request<ApiResponse<VegetationType[]>>(
        `/api/projects/${projectId}/vegetationTypes/batch`,
        { method: "POST", body: batch }
      ),
    onSuccess: (resp) => {
      queryClient.setQueryData(["vegetationTypes", projectId], resp);
      queryClient.invalidateQueries({ queryKey: ["vegetationTypes"] });
      queryClient.invalidateQueries({ queryKey: ["vegetationSources"] });
      invalidateCuring(queryClient, projectId);
      queryClient.invalidateQueries({ queryKey: ["strategy"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      bumpVegetationTileVersion(queryClient, projectId);
    },
  });
};
